/**
 * Prompt Section Component
 * 
 * This component provides a text input area with auto-resizing functionality and optional
 * prompt library cards. It handles user input, sends messages, and manages textarea height
 * dynamically based on content.
 * 
 * Features:
 * - Auto-resizing textarea with min/max height constraints
 * - Prompt library cards for quick access to predefined prompts
 * - Enter key to send (Shift+Enter for new line)
 * - Disabled state during message processing
 */

import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2, AfterViewInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { PromptLibraryItem } from '../../models/prompt.models';
import { AgentConfigService } from '../../../app/agents/services/agent-config/agent-config.service';
import { TranslateService } from '@ngx-translate/core';
import { ContenteditableModelDirective } from '../../../directives/contenteditable-textarea/contenteditable-model.directive';
import { ConfigurationService } from '../../../services/configuration/configuration.service';
import { RegulationsHubPromptLibraryStore, mapRegRadarSliceToPromptLibrary } from '../../../app/agents/services/regulations-hub-prompt-library/regulations-hub-prompt-library.store';

@Component({
  selector: 'app-prompt-section',
  templateUrl: './prompt-section.component.html',
  styleUrls: ['./prompt-section.component.scss']
})
export class PromptSectionComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  isRegRader = true
  isRegSelect = true

  @Input() agentId: string = '';
  @Input() loaderShow: boolean = false;
  @Input() isNewChat: boolean = true;
  /** For prompt library: dataset list and selection (e.g. from chatbot ref). */
  @Input() vectorDbDatasets: any[] = [];
  @Input() selectedVectorDbDataset: any;
  @Input() IsResponseStreaming: boolean = false;
  @Input() domainName: string = '';
  @Input() tenantId: any;
  @Input() userId: any;
  /** For domain @-mentions in the text area (e.g. from chatbot ref). */
  @Input() allDomainNames: string[] = [];
  @Input() isDomainManagementEnabled: boolean = false;
  @Output() sendRequest = new EventEmitter<string>();
  @Output() displayPromptLibraryPopup = new EventEmitter<boolean>();
  @Output() promptLibraryNewSelectedDataset = new EventEmitter<any>();
  @Output() promptLibraryClickNewChat = new EventEmitter<boolean>();
  @Output() promptLibraryScrollBottom = new EventEmitter<void>();
  @Output() suggestionSelected = new EventEmitter<string>();
  @Output() domainInserted = new EventEmitter<void>();
  
  @ViewChild('promptTextarea') promptTextarea!: ElementRef<HTMLDivElement>;
  @ViewChild(ContenteditableModelDirective) contenteditableDirective?: ContenteditableModelDirective;

  /** Native element for domain-switch (expects HTMLElement, not ElementRef). */
  get textareaElementForDomainSwitch(): HTMLElement | null {
    return this.promptTextarea?.nativeElement ?? null;
  }


  /** 
   * Removes trailing "Agent" suffix in multiple languages:
   * - English: "Agent" (case-insensitive)
   * - Chinese: "代理"
   * - Japanese: "エージェント"
   */
  private stripAgentSuffix(raw: string): string {
    let result = raw.trim();
    
    // Suffixes to remove (order: longest first to avoid partial matches)
    const suffixes = [
      'エージェント',  // Japanese
      '代理',          // Chinese
      'agent'          // English (case-insensitive check below)
    ];
    
    for (const suffix of suffixes) {
      if (result.length < suffix.length) continue;
      
      // For English, do case-insensitive comparison
      if (suffix === 'agent') {
        const lower = result.toLowerCase();
        if (lower.endsWith(suffix)) {
          result = result.slice(0, result.length - suffix.length).trimEnd();
          break;
        }
      } else {
        // For CJK, do exact match
        if (result.endsWith(suffix)) {
          result = result.slice(0, result.length - suffix.length).trimEnd();
          break;
        }
      }
    }
    
    return result;
  }

  /** Placeholder for the input: "Ask {Name}" from agent config (suffix " Agent" removed), or "Ask Freya" when no agent. */
  get inputPlaceholder(): string {
    if (!this.agentId) return 'Ask Freya';
    const config = this.agentConfigService.getAgentConfig(this.agentId);
    const raw = config?.display_name || config?.name || '';
    const translated = raw ? this.translate.instant(raw) : '';
    const name = this.stripAgentSuffix(translated);
    const askText = this.translate.instant('TEXT_AREA_PLACEHOLDER') || 'Ask';
    return name ? `${askText} ${name}` : `${askText} Freya`;
  }
  
  txtChat: string = '';
  promptLibrary: PromptLibraryItem[] = [];
  PromptLibraryPopup : boolean = false
  micActive: boolean = false;
  /** True when domain @-mention dropdown is open; Enter should select domain, not send message. */
  isDomainDropdownOpen: boolean = false;
  IsCustomErrorMessageVisible: boolean = false;
  CustomErrorMessageText: string = "";
  isRetrievalModeFeatureEnabled: boolean = false;
  
  private readonly minHeight: number = 100;
  private readonly maxHeight: number = 240;
  private readonly isAtMaxHeight: boolean = false;
  private readonly lastScrollHeight: number = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly renderer: Renderer2,
    private readonly logger: LoggerService,
    public agentConfigService: AgentConfigService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly regulationsHubPromptLibraryStore: RegulationsHubPromptLibraryStore,
    private readonly configService: ConfigurationService
  ) {}

  /**
   * Component initialization - loads prompt library for the current agent
   */
  async ngOnInit(): Promise<void> {
    try {
      this.isRetrievalModeFeatureEnabled = await this.configService.isRetrievalModeEnabled();

      this.regulationsHubPromptLibraryStore.hubSlice$
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (this.agentId === 'regulations_agent') {
            void this.loadPromptLibrary(this.agentId);
          }
        });

      this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.agentId === 'regulations_agent' && this.regulationsHubPromptLibraryStore.getHubSlice() != null) {
          void this.loadPromptLibrary(this.agentId);
        }
      });

      if (this.agentId) {
        await this.loadPromptLibrary(this.agentId);
      }
    } catch (error) {
      this.logger.logInfo(`Error in ngOnInit: ${error}`, 'PromptSectionComponent.ngOnInit');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  /**
   * After view initialization - directive handles initial textarea setup (height, placeholder)
   */
  ngAfterViewInit(): void {
     // ContenteditableModelDirective initializes height and placeholder
  }

  /**
   * Handles component input changes - reloads prompt library when agent changes
   * @param changes - SimpleChanges object containing changed properties
   */
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    try {
      if (changes['agentId'] && !changes['agentId'].firstChange) {
        await this.loadPromptLibrary(this.agentId);
      }
    } catch (error) {
      this.logger.logInfo(`Error in ngOnChanges: ${error}`, 'PromptSectionComponent.ngOnChanges');
    }
  }

  /**
   * Loads prompt library configuration for the specified agent from JSON config file
   * @param agentId - The ID of the agent to load prompts for
   */
  private async loadPromptLibrary(agentId: string): Promise<void> {
    try {
      if (agentId === 'regulations_agent') {
        const hubSlice = this.regulationsHubPromptLibraryStore.getHubSlice();

        if (hubSlice?.app_config_data.length == 0){
          this.isRegSelect =false
        }
        if (hubSlice?.reg_radar_data.length == 0){
          this.isRegRader =false
        }

        if (hubSlice != null) {
          const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
          this.promptLibrary = mapRegRadarSliceToPromptLibrary(hubSlice, lang);
          this.cdr.markForCheck();
          return;
        }
      }


      // Use centralized config service (already loaded at app startup)
      const agent = this.agentConfigService.getAgentConfig(agentId);
      
      if (agent && agent.prompt_library) {
        this.promptLibrary = agent.prompt_library;
      } else {
        this.promptLibrary = [];
      }
    } catch (error) {
      this.logger.logInfo(`Error loading prompt library: ${error}`, 'PromptSectionComponent.loadPromptLibrary');
      this.promptLibrary = [];
    }
  }

  /**
   * Handles click on a prompt card - populates textarea with selected prompt
   * ngModel + ContenteditableModelDirective update DOM and height
   * @param prompt - The prompt library item that was clicked
   */
  onPromptClick(prompt: PromptLibraryItem): void {
    try {
      this.txtChat = prompt.literalContent
        ? prompt.prompt
        : this.translate.instant(prompt.prompt);
      
      setTimeout(() => {
        if (this.promptTextarea?.nativeElement) {
          this.promptTextarea.nativeElement.focus();
        }
      }, 0);
    } catch (error) {
      this.logger.logInfo(`Error in onPromptClick: ${error}`, 'PromptSectionComponent.onPromptClick');
    }
  }

  /**
   * Handles Enter key press - sends message on Enter, allows Shift+Enter for new line.
   * Matches chatbot behavior: IME composition and empty text edge cases.
   * @param event - Keyboard event from textarea
   */
  onEnterPress(event: KeyboardEvent): void {
    try {
      if (event.key !== 'Enter' || this.loaderShow) return;

      if (event.shiftKey) {
        // Shift+Enter: let default newline happen (directive handles height)
        return;
      }

      // When domain @-mention dropdown is open, Enter selects the domain (handled by domain-switch); do not send the message.
      if (this.isDomainDropdownOpen) {
        event.preventDefault();
        return;
      }

      // Enter without Shift: don't send during IME composition (same as chatbot)
      const target = event.target as HTMLElement;
      if (target?.hasAttribute('data-ime-composing')) {
        return;
      }

      event.preventDefault();
      if (!this.txtChat?.trim()) return;

      this.sendMessage();
    } catch (error) {
      this.logger.logInfo(`Error in onEnterPress: ${error}`, 'PromptSectionComponent.onEnterPress');
    }
  }


  /**
   * Sends the message by emitting the text to parent component.
   * Clearing txtChat updates the contenteditable via ContenteditableModelDirective (writeValue + adjustHeight).
   */
  sendMessage(): void {
    try {
      if (!this.txtChat?.trim() || this.loaderShow) return;
      this.sendRequest.emit(this.txtChat);
      this.txtChat = '';
    } catch (error) {
      this.logger.logInfo(`Error in sendMessage: ${error}`, 'PromptSectionComponent.sendMessage');
    }
  }

  /**
   * Public helper to clear the input programmatically (agent switches, history selection).
   * ContenteditableModelDirective updates DOM and height when txtChat is set to ''.
   */
  clearInput(): void {
    try {
      this.txtChat = '';
      this.contenteditableDirective?.resetHeight();
    } catch (error) {
      this.logger.logInfo(`Error in clearInput: ${error}`, 'PromptSectionComponent.clearInput');
    }
  }

  /**
   * Get placeholder text for prompt input
   * @returns Placeholder text like "Ask PAC" (removes "Agent" from name, translated)
   */
  get placeholderText(): string {
    try {
      return this.inputPlaceholder;
      
    } catch (error) {
      this.logger.logInfo(`Error in placeholderText: ${error}`, 'PromptSectionComponent.placeholderText');
      return 'Ask Freya';
    }
  }

  /**
   * Opens the Prompt Library overlay. Blurs the contenteditable first and defers opening
   * to the next task to avoid page freeze: with contenteditable focused, opening the
   * overlay in the same synchronous turn can trigger focus/selection handling, CD, and
   * the directive's MutationObserver (which watches .prompt-section) in a way that blocks the main thread.
   */
  onPromptLibrary(): void {
    if (this.promptTextarea?.nativeElement) {
      this.promptTextarea.nativeElement.blur();
    }
    setTimeout(() => {
      this.PromptLibraryPopup = true;
      this.displayPromptLibraryPopup.emit(true);
      this.cdr.detectChanges();
    }, 0);
  }

  /** Handle keydown on prompt overlay (Escape to close). */
  onPromptOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.PromptLibraryPopup = false;
    }
  }

  /** Handle keydown on prompt popup wrapper (Escape closes via overlay). */
  onPromptPopupWrapperKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.PromptLibraryPopup = false;
    }
  }

  /**
   * Handles question selected from prompt library: fills input, then send is triggered by sendRequestEmitted.
   */
  onPromptLibraryQuestionSelected(question: string): void {
    this.onMicSpeechUpdate(question);
  }

  /**
   * Called when prompt library asks to send the selected question (same flow as chatbot).
   * Defer send so txtChat is updated from uploadQuestionSelected before sending.
   */
  onSendRequestFromPromptLibrary(): void {
    setTimeout(() => this.sendMessage(), 0);
  }

  /**
   * Called after domain-switch inserts a domain @-mention: sync txtChat from contenteditable and adjust height.
   */
  onDomainInserted(): void {
    try {
      if (this.promptTextarea?.nativeElement) {
        const textContent = this.promptTextarea.nativeElement.textContent || '';
        this.txtChat = textContent;
      }
      setTimeout(() => {
        this.contenteditableDirective?.adjustHeight();
        if (this.promptTextarea?.nativeElement) {
          this.promptTextarea.nativeElement.focus();
        }
        this.cdr.detectChanges();
      }, 0);
      this.domainInserted.emit();
    } catch (error) {
      this.logger.logInfo(`Error in onDomainInserted: ${error}`, 'PromptSectionComponent.onDomainInserted');
    }
  }

  onMicSpeechUpdate(text: string): void {
    this.txtChat = text;
    if (this.promptTextarea?.nativeElement) {
      this.promptTextarea.nativeElement.textContent = text;
    }
    setTimeout(() => {
      this.contenteditableDirective?.adjustHeight();
      if (this.promptTextarea?.nativeElement) {
        this.promptTextarea.nativeElement.focus();
      }
    }, 0);
    this.cdr.detectChanges();
  }

  onMicStatusUpdate(status: boolean): void {
    this.micActive = status;
    this.cdr.detectChanges();
  }

    /**
   * Shows a toast message when microphone permission is denied
   */
  onMicSpeechError(error: string): void {
    if (error === 'MIC_PERMISSION_DENIED' || error === 'MIC_PERMISSION_NETWORK_ERROR' || error === 'MIC_UNKNOWN_ERROR') {
      this.IsCustomErrorMessageVisible = true;
      const translatedApologyMsg = this.translate.instant(error);
      this.CustomErrorMessageText = translatedApologyMsg;
      setTimeout(() => {
          this.IsCustomErrorMessageVisible = false;
      }, 4000);
      this.cdr.detectChanges();
    }
  }
}



